import { create } from 'zustand'
import { authApi } from '../services/api'
import { reconnectSocket, disconnectSocket, getSocket } from '../services/socket'
import { sounds } from '../services/sounds'
import { mediaManager } from '../services/mediaManager'
import { useVoiceStore } from './voiceStore'
import { useLivekitStore } from './livekitStore'
import { dialog } from '../services/dialog'
import { useGuestStore } from './guestStore'
import { useToastStore } from './toastStore'
import { useChannelStore } from '@/shell/stores/channelStore'
import { useDmStore } from '@os/messenger/web/stores/dmStore'
import type { User } from '../types'

// OSPROD-180: a 2FA-enabled account needs an SMS code after the password.
export type LoginResult =
  | { status: 'ok' }
  | { status: 'awaiting_2fa'; challengeId: string; phoneHint: string }

const TRUSTED_DEVICE_KEY = 'trustedDeviceToken'

// ─── OSPROD-248: mic-toggle robustness helpers ──────────────────────────────
// Prod 2026-07-01 (DEV Conference room): repeated "Microphone toggle failed"
// toasts — the toggle's PUT /auth/me never reached the server (client-side
// axios failure / stall; axios has NO default timeout, so a hung request also
// left micToggleInFlight stuck and the button dead). The mute itself was
// gated BEHIND that HTTP round-trip, so the mic never turned off. The rework
// below applies the LiveKit runtime change FIRST and persists after, bounded
// by these timeouts.

// Runtime op bound. mediaManager already retries internally (3× backoff), so
// this only guards a genuinely wedged single-flight chain — the op itself
// keeps running in the background and the 1s health reconciler converges it.
const MIC_RUNTIME_TIMEOUT_MS = 10_000
// Persistence bound per attempt (+1 retry below). PUT /auth/me is idempotent.
const MIC_PERSIST_TIMEOUT_MS = 8_000
const MIC_PERSIST_RETRY_DELAY_MS = 1_000

function withTimeout<T>(p: Promise<T>, ms: number, tag: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(tag)), ms)
    p.then(
      (v) => { clearTimeout(t); resolve(v) },
      (e) => { clearTimeout(t); reject(e) },
    )
  })
}

// One retry: transient network blips / the token-refresh single-flight race
// (two renderer windows refreshing in parallel) heal within a second.
async function updateMeWithRetry(data: Partial<User>): Promise<{ data: User }> {
  try {
    return await withTimeout(authApi.updateMe(data), MIC_PERSIST_TIMEOUT_MS, 'mic-persist-timeout')
  } catch (first) {
    console.warn('[authStore] settings persist attempt 1 failed, retrying once:', first)
    await new Promise((r) => setTimeout(r, MIC_PERSIST_RETRY_DELAY_MS))
    return await withTimeout(authApi.updateMe(data), MIC_PERSIST_TIMEOUT_MS, 'mic-persist-timeout-retry')
  }
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  preDeafenMuted: boolean
  // OSPROD-180: login resolves to 'ok' (session established) or
  // 'awaiting_2fa' (caller must collect the SMS code, then call login2fa).
  login: (email: string, password: string) => Promise<LoginResult>
  login2fa: (challengeId: string, code: string, trustDevice: boolean) => Promise<void>
  loginWithGoogle: (idToken: string) => Promise<void>
  // ViewSX self-service signup: exchange a one-time '#signup=<code>' for a
  // normal login session (Login.tsx consumes the fragment on mount).
  // Resolves the provisioned workspace id so the caller can navigate straight in.
  signupExchange: (code: string) => Promise<{ serverId: string }>
  registerStart: (email: string, username: string, firstName: string, lastName: string, position: string, birthday: string, password: string, inviteCode: string) => Promise<{ sessionId: string }>
  registerVerify: (sessionId: string, code: string) => Promise<void>
  registerResend: (sessionId: string) => Promise<void>
  logout: () => Promise<void>
  fetchUser: () => Promise<void>
  updateUser: (data: Partial<User>) => Promise<void>
  toggleMute: () => Promise<void>
  toggleDeafen: () => Promise<void>
  setSkipVoiceSwitchConfirm: (on: boolean) => Promise<void>
  // OSPROD-108: pin/unpin a voice room in the sidebar (personal pref).
  togglePinnedVoiceRoom: (channelId: string) => Promise<void>
  // Persisted force-mute applied on every deliberate voice-channel join.
  muteForJoin: () => void
  // Persisted unmute intent for a phone-admit join (admit = answer the phone).
  unmuteForJoin: () => void
  // unmuteForJoin + immediate mic publish when already connected to a room.
  unmuteForPhoneAdmit: () => void
  // Set/clear the single "home" voice channel (channelId=null clears it).
  setHomeVoiceChannel: (channelId: string | null, serverId: string | null) => Promise<void>
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  preDeafenMuted: false,

  login: async (email, password) => {
    const trustedDeviceToken = localStorage.getItem(TRUSTED_DEVICE_KEY) || undefined
    const res = await authApi.login({ email, password, trustedDeviceToken })
    // OSPROD-180: a 2FA-enabled account returns 202 with a challenge and
    // NO token — hand the challenge back so the page collects the code.
    if (res.status === 202 || res.data?.status === 'awaiting_2fa') {
      return { status: 'awaiting_2fa', challengeId: res.data.challengeId, phoneHint: res.data.phoneHint }
    }
    localStorage.setItem('token', res.data.token)
    // OSPROD-240: persist the refresh token so an expired access token is
    // silently renewed (api.ts interceptor) instead of dropping the session.
    if (res.data.refreshToken) localStorage.setItem('refreshToken', res.data.refreshToken)
    // OSPROD-160: clear any stale guest session so a now-authenticated
    // user is never treated as a guest in voice (LiveKit identity guest_*).
    useGuestStore.getState().clearGuest()
    reconnectSocket()
    set({ user: res.data.user, isAuthenticated: true })
    return { status: 'ok' }
  },

  // OSPROD-180: second login step — submit the SMS code (+ optionally
  // remember this device). On success the session is established exactly
  // like a normal login.
  login2fa: async (challengeId, code, trustDevice) => {
    const res = await authApi.login2fa({ challengeId, code, trustDevice })
    localStorage.setItem('token', res.data.token)
    // OSPROD-240: persist the refresh token so an expired access token is
    // silently renewed (api.ts interceptor) instead of dropping the session.
    if (res.data.refreshToken) localStorage.setItem('refreshToken', res.data.refreshToken)
    if (res.data.trustedDeviceToken) {
      localStorage.setItem(TRUSTED_DEVICE_KEY, res.data.trustedDeviceToken)
    }
    useGuestStore.getState().clearGuest()
    reconnectSocket()
    set({ user: res.data.user, isAuthenticated: true })
  },

  loginWithGoogle: async (idToken) => {
    const res = await authApi.loginWithGoogle({ idToken })
    localStorage.setItem('token', res.data.token)
    // OSPROD-240: persist the refresh token so an expired access token is
    // silently renewed (api.ts interceptor) instead of dropping the session.
    if (res.data.refreshToken) localStorage.setItem('refreshToken', res.data.refreshToken)
    // OSPROD-160: see login() — drop stale guest session on sign-in.
    useGuestStore.getState().clearGuest()
    reconnectSocket()
    set({ user: res.data.user, isAuthenticated: true })
  },

  // ViewSX self-service signup: same login-success tail as login()/login2fa()
  // /loginWithGoogle() above (store tokens, drop stale guest state, reconnect
  // the socket, set the authenticated user) — the only difference is the
  // endpoint and that it also hands back the provisioned serverId.
  signupExchange: async (code) => {
    const res = await authApi.signupExchange(code)
    localStorage.setItem('token', res.data.token)
    if (res.data.refreshToken) localStorage.setItem('refreshToken', res.data.refreshToken)
    useGuestStore.getState().clearGuest()
    reconnectSocket()
    set({ user: res.data.user, isAuthenticated: true })
    return { serverId: res.data.serverId }
  },

  registerStart: async (email, username, firstName, lastName, position, birthday, password, inviteCode) => {
    const res = await authApi.registerStart({ email, username, firstName, lastName, position, birthday, password, inviteCode })
    return { sessionId: res.data.sessionId as string }
  },

  registerVerify: async (sessionId, code) => {
    const res = await authApi.registerVerify({ sessionId, code })
    localStorage.setItem('token', res.data.token)
    // OSPROD-240: persist the refresh token so an expired access token is
    // silently renewed (api.ts interceptor) instead of dropping the session.
    if (res.data.refreshToken) localStorage.setItem('refreshToken', res.data.refreshToken)
    reconnectSocket()
    set({ user: res.data.user, isAuthenticated: true })
  },

  registerResend: async (sessionId) => {
    await authApi.registerResend({ sessionId })
  },

  logout: async () => {
    try { await authApi.logout() } catch { /* ignore — clear local state regardless */ }
    // Tear down active calls/rooms BEFORE we drop auth — otherwise:
    //   - LiveKit room stays connected, mic still publishes, other side
    //     keeps hearing the user. Especially nasty for guests because the
    //     UI is gone but the voice track lingers.
    //   - Server's voice:leave / dm:call:end never fires, so participant
    //     lists on every other client still show this user "in the room".
    // Both stores' .disconnect() send the leave socket events as part of
    // their teardown, so calling them here is enough.
    try {
      const voiceLeave = useVoiceStore.getState().leaveChannel
      if (useVoiceStore.getState().currentChannelId) voiceLeave()
    } catch { /* noop */ }
    try { useLivekitStore.getState().disconnect() } catch { /* noop */ }
    // DM call disconnect is fire-and-forget — we can't await here.
    import('@os/messenger/web/stores/dmCallStore').then(({ useDmCallStore }) => {
      try {
        const dmState = useDmCallStore.getState()
        if (dmState.callState !== 'idle') {
          dmState.disconnect().catch(() => {})
          dmState.setCallState('idle')
        }
      } catch { /* noop */ }
    }).catch(() => {})

    // Guest sessions live under their own zustand store + four extra
    // localStorage keys and an in-memory `isGuest: true` flag. Delegate
    // to clearGuest() so every key + the flag drop together.
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    useGuestStore.getState().clearGuest()
    // Tear down per-user caches so a different account signing in on
    // the same browser doesn't see the previous user's pending bubbles
    // (which contain their typed text), confirmed messages, unread
    // state, DM list, or folder preferences. Each store nukes its own
    // localStorage blob too — see channelStore.resetForLogout +
    // dmStore.resetForLogout.
    try { useChannelStore.getState().resetForLogout() } catch { /* noop */ }
    try { useDmStore.getState().resetForLogout() } catch { /* noop */ }
    disconnectSocket()
    set({ user: null, isAuthenticated: false })
  },

  fetchUser: async () => {
    const token = localStorage.getItem('token')
    const guestToken = localStorage.getItem('guestToken')
    if (token) {
      try {
        const res = await authApi.me()
        set({ user: res.data, isAuthenticated: true, isLoading: false })
        return
      } catch {
        // Reached only if the interceptor's refresh also failed (or there
        // was no refresh token) — the session is gone; clear both tokens.
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
      }
    }
    if (guestToken) {
      const guestName = localStorage.getItem('guestName') || 'Guest'
      set({
        user: {
          id: guestToken.replace(/^guest:/, ''),
          email: `guest_${guestToken.replace(/^guest:/, '')}@rx2go.ai`,
          username: guestName,
          role: 'guest',
          isAdmin: false,
          status: 'ONLINE',
          settings: {},
        } as User,
        isAuthenticated: true,
        isLoading: false,
      })
      return
    }
    set({ user: null, isAuthenticated: false, isLoading: false })
  },

  updateUser: async (data) => {
    const res = await authApi.updateMe(data)
    set({ user: res.data })
  },

  toggleMute: async () => {
    const user = useAuthStore.getState().user
    if (!user) return
    const { isGuest } = useGuestStore.getState()
    const room = useLivekitStore.getState().room

    // OSPROD-57: drop concurrent clicks. The mic button reads
    // livekitStore.micToggleInFlight so the lock here uses the same flag —
    // VoiceChannelControls disables the button visually for both paths.
    if (useLivekitStore.getState().micToggleInFlight) return

    // Guest path: direct LiveKit toggle, no DB
    if (isGuest) {
      if (!room) return
      const currentlyEnabled = room.localParticipant.isMicrophoneEnabled
      const nextEnabled = !currentlyEnabled
      // Guests have no DB row, but the health reconciler still follows the
      // in-memory settings.isMuted as `expectedMuted` (and muteForJoin sets it
      // true on every join). Keep it in step with the runtime, or the
      // reconciler re-mutes the guest seconds after they unmute
      // ('health-audio-desync', prod 2026-10-15).
      const setGuestIntentMuted = (isMuted: boolean) => {
        const u = useAuthStore.getState().user
        if (!u) return
        set({ user: { ...u, settings: { ...(u.settings ?? {}), isMuted } } })
      }
      setGuestIntentMuted(!nextEnabled)
      // Optimistic UI — toggle via LiveKit flag immediately
      useLivekitStore.setState({ micToggleInFlight: true, isAudioTrackReady: nextEnabled })
      withTimeout(mediaManager.setMicrophoneEnabled(room, nextEnabled), MIC_RUNTIME_TIMEOUT_MS, 'mic-runtime-timeout').then((actual) => {
        useLivekitStore.setState({ isAudioTrackReady: actual, micToggleInFlight: false })
      }).catch((err) => {
        // OSPROD-248: resync from GROUND TRUTH (not the pre-click snapshot —
        // the op may have partially applied) + record the failure reason.
        mediaManager.noteMicToggleFailure('guest-runtime', err)
        const enabledNow = !!room.localParticipant.isMicrophoneEnabled
        setGuestIntentMuted(!enabledNow)
        useLivekitStore.setState({
          isAudioTrackReady: enabledNow,
          micToggleInFlight: false,
        })
      })
      const { currentChannelId } = useVoiceStore.getState()
      if (currentChannelId) {
        getSocket().emit('voice:state:update', { channelId: currentChannelId, isMuted: !nextEnabled })
      }
      // OSPROD-61: plain mic mute/unmute does NOT touch BRB — BRB is
      // coupled to deafen only (the "I've stepped away" sequence).
      if (nextEnabled) sounds.unmute()
      else sounds.mute()
      return
    }

    // Force-mute lock: an admin has locked this user's mic in the current
    // channel. Block any attempt that would end up UNMUTED and explain why.
    const vs = useVoiceStore.getState()
    const settingsNow = user.settings ?? {}
    const wouldUnmute = !!settingsNow.isMuted // currently muted → this toggle unmutes
    if (wouldUnmute && vs.isForceMutedHere(vs.currentChannelId)) {
      await dialog.alert({
        title: 'Muted by an administrator',
        message: "An administrator muted you. You can't unmute yourself in this channel.",
      })
      return
    }

    const settings = user.settings ?? {}
    const nextMuted = !settings.isMuted
    const isDeafened = settings.isDeafened ?? false
    // The mic-button shortcut also lifts deafen when you unmute while
    // deafened. That undeafen is what clears BRB (BRB tracks deafen,
    // not mute) — see toggleDeafen for the other directions.
    const willUndeafen = !nextMuted && isDeafened

    // Optimistic update — instant UI reflect
    const optimisticSettings = willUndeafen
      ? { ...settings, isMuted: false, isDeafened: false }
      : { ...settings, isMuted: nextMuted }
    set({ user: { ...user, settings: optimisticSettings } })
    useLivekitStore.setState({ micToggleInFlight: true })
    if (willUndeafen) {
      useVoiceStore.getState().setLocalAway(false)
    }

    // Background async: LiveKit FIRST, then DB (OSPROD-248).
    //
    // The previous order gated the actual LiveKit mute/unmute BEHIND a
    // successful PUT /auth/me. Prod 2026-07-01 (DEV Conference room): the
    // client's PUT never reached the server (nginx showed zero /auth/me hits
    // during the whole incident while all other traffic flowed), so every
    // click ended in the generic toast, the mic NEVER muted, and the
    // optimistic revert + 1s health reconciler produced a 14-minute
    // intent↔runtime flap (4× health-audio-desync recoveries in voice_diag).
    // Muting is privacy-critical — it must not depend on an HTTP round-trip.
    // New order: (1) flip the runtime mic, (2) broadcast over the socket
    // (alive even when HTTP is broken), (3) persist with timeout + one retry.
    const runAsync = async () => {
      const targetEnabled = willUndeafen ? true : (!nextMuted && !isDeafened)
      const settingsPayload = willUndeafen
        ? { isMuted: false, isDeafened: false }
        : { isMuted: nextMuted }
      try {
        // 1) Runtime first. Volume restore on undeafen is handled by the Web
        //    Audio GainNode in LiveKitBackgroundAudio — it reads `isDeafened`
        //    from this store (already flipped optimistically above) and snaps
        //    gain.value back to peerVolume. (participant.setVolume here used
        //    to crash on boosted >1× sliders and double-played the hidden
        //    keep-alive <audio> — see the big-call "2 voices" report.)
        if (room) {
          try {
            const actualEnabled = await withTimeout(
              mediaManager.setMicrophoneEnabled(room, targetEnabled),
              MIC_RUNTIME_TIMEOUT_MS,
              'mic-runtime-timeout',
            )
            useLivekitStore.setState({ isAudioTrackReady: actualEnabled })
          } catch (err) {
            // Runtime toggle failed/timed out → revert the optimistic intent
            // to the PRE-TOGGLE settings and RESYNC the UI from ground truth
            // instead of leaving a desync (OSPROD-248). Telemetry carries the
            // underlying reason (voice_diag rawStats type='mic-toggle-fail').
            mediaManager.noteMicToggleFailure('runtime', err)
            console.error('[authStore] toggleMute runtime failed, reverting intent:', err)
            useToastStore.getState().showToast({
              id: `mic-toggle-${Date.now()}`,
              type: 'error',
              title: 'Microphone toggle failed',
              body: 'Please try again.',
            })
            set({ user: { ...user, settings } })
            if (willUndeafen) useVoiceStore.getState().setLocalAway(true)
            // mute-coupling guard (prod 2026-06-25 health-audio-desync
            // incident): the runtime may have PARTIALLY applied. Sync the UI
            // to the actual participant state, then re-apply the runtime to
            // MATCH the reverted persisted intent so the 1s reconciler
            // (which follows settings.isMuted) has nothing to fight.
            const liveRoom = useLivekitStore.getState().room
            if (liveRoom) {
              useLivekitStore.setState({ isAudioTrackReady: !!liveRoom.localParticipant.isMicrophoneEnabled })
              const shouldEnable = !(settings.isMuted ?? false) && !(settings.isDeafened ?? false)
              try {
                const actual = await mediaManager.setMicrophoneEnabled(liveRoom, shouldEnable)
                useLivekitStore.setState({ isAudioTrackReady: actual })
              } catch {
                // best-effort — the health-check reconciler owns final convergence
              }
            }
            return
          }
        }

        // 2) Socket broadcast — independent of the HTTP persistence, so other
        //    participants see the state even when PUT /auth/me is failing.
        const { currentChannelId } = useVoiceStore.getState()
        if (currentChannelId) {
          getSocket().emit('voice:state:update', { channelId: currentChannelId, ...settingsPayload })
        }
        if (targetEnabled) sounds.unmute()
        else sounds.mute()

        // 3) Persist (timeout + one retry). The mic is ALREADY in the state
        //    the user chose — a persistence failure must NOT revert it. Keep
        //    the optimistic settings as the local intent (the 1s reconciler
        //    follows the LOCAL settings, so runtime and intent stay aligned);
        //    record the reason in telemetry. No error toast here: the toggle
        //    the user asked for HAS happened.
        try {
          const res = await updateMeWithRetry({ settings: settingsPayload })
          set({ user: res.data })
        } catch (err) {
          mediaManager.noteMicToggleFailure('persist', err)
          console.error('[authStore] toggleMute persisted-settings save failed — mic state applied locally, will sync on next settings write:', err)
        }
      } finally {
        useLivekitStore.setState({ micToggleInFlight: false })
      }
    }

    runAsync()
  },

  // OSPROD-104: persist the "don't ask again" voice-switch preference.
  // Optimistic update, then authApi.updateMe merges it into user.settings
  // server-side (same path as isMuted/isDeafened). Sends the merged
  // settings object so it's safe regardless of merge-vs-replace semantics.
  setSkipVoiceSwitchConfirm: async (on) => {
    const user = useAuthStore.getState().user
    if (!user) return
    const settings = user.settings ?? {}
    set({ user: { ...user, settings: { ...settings, skipVoiceSwitchConfirm: on } } })
    try {
      const res = await authApi.updateMe({ settings: { ...settings, skipVoiceSwitchConfirm: on } })
      set({ user: res.data })
    } catch (err) {
      console.error('setSkipVoiceSwitchConfirm error:', err)
      useToastStore.getState().showToast({
        id: `voiceswitch-${Date.now()}`,
        type: 'error',
        title: 'Setting not saved',
        body: 'Please try again.',
      })
      set({ user: { ...user, settings } })
    }
  },

  // OSPROD-108: pin/unpin a voice room in the sidebar. Pins are a personal
  // preference stored as a channelId list in user.settings.pinnedVoiceRooms,
  // persisted via updateMe like the other settings flags. Optimistic update
  // with a full-settings spread so it's safe under merge-or-replace.
  togglePinnedVoiceRoom: async (channelId) => {
    const user = useAuthStore.getState().user
    if (!user) return
    const settings = user.settings ?? {}
    const current: string[] = Array.isArray(settings.pinnedVoiceRooms) ? settings.pinnedVoiceRooms : []
    const next = current.includes(channelId)
      ? current.filter((id) => id !== channelId)
      : [...current, channelId]
    set({ user: { ...user, settings: { ...settings, pinnedVoiceRooms: next } } })
    try {
      const res = await authApi.updateMe({ settings: { ...settings, pinnedVoiceRooms: next } })
      set({ user: res.data })
    } catch (err) {
      console.error('togglePinnedVoiceRoom error:', err)
      useToastStore.getState().showToast({
        id: `pinroom-${Date.now()}`,
        type: 'error',
        title: 'Pin not saved',
        body: 'Please try again.',
      })
      set({ user: { ...user, settings } })
    }
  },

  // Force-mute applied on every deliberate voice join (launch policy — see
  // voiceStore.joinChannel). It MUST persist, not just set isMuted in memory:
  // the 5s health-check reconciler in mediaManager follows settings.isMuted as
  // `expectedMuted`, and any fetchUser()/updateMe() that overwrites user.settings
  // with the server's (stale isMuted=false) flips expectedMuted back to false,
  // so the reconciler RE-ENABLES the mic mid-call (prod 2026-06-09: repeated
  // `health-audio-desync` unmutes while the user sat muted). Optimistic in-memory
  // set + fire-and-forget persist (full-settings spread) so server + memory agree
  // and the reconciler keeps the mic off until the user unmutes themselves.
  muteForJoin: () => {
    const user = useAuthStore.getState().user
    if (!user) return
    const settings = user.settings ?? {}
    if (settings.isMuted) return // already muted — nothing to flip or persist
    set({ user: { ...user, settings: { ...settings, isMuted: true } } })
    authApi.updateMe({ settings: { ...settings, isMuted: true } })
      .then((res) => set({ user: res.data }))
      .catch(() => { /* optimistic mute already applied; reconciler honours it */ })
  },

  // Mirror of muteForJoin for the phone-admit flow (admit = answer the
  // phone). The SIP gateway only answers the PSTN leg once it can subscribe
  // to a remote AUDIO track in the room, and our mute model publishes NO
  // track while muted — an all-muted room leaves the caller hearing ringback
  // until the dial times out. Same persistence rules as muteForJoin: the 5s
  // reconciler follows settings.isMuted, so the flip must be persisted or a
  // later fetchUser would re-mute mid-call. Lifts deafen too (you can't
  // answer a call you can't hear).
  unmuteForJoin: () => {
    const user = useAuthStore.getState().user
    if (!user) return
    const settings = user.settings ?? {}
    if (!settings.isMuted && !settings.isDeafened) return
    const next = { ...settings, isMuted: false, isDeafened: false }
    set({ user: { ...user, settings: next } })
    if (settings.isDeafened) useVoiceStore.getState().setLocalAway(false)
    authApi.updateMe({ settings: next })
      .then((res) => set({ user: res.data }))
      .catch(() => { /* optimistic unmute already applied; reconciler honours it */ })
  },

  // Phone-admit while ALREADY connected to the channel: flip the persisted
  // intent AND publish the mic right now — the SIP leg is waiting on the
  // subscription, so waiting up to 5s for the reconciler wastes ring time.
  unmuteForPhoneAdmit: () => {
    useAuthStore.getState().unmuteForJoin()
    const room = useLivekitStore.getState().room
    if (!room) return
    mediaManager.setMicrophoneEnabled(room, true)
      .then((actual) => useLivekitStore.setState({ isAudioTrackReady: actual }))
      .catch(() => { /* reconciler retries against settings.isMuted=false */ })
    const { currentChannelId } = useVoiceStore.getState()
    if (currentChannelId) {
      getSocket().emit('voice:state:update', { channelId: currentChannelId, isMuted: false, isDeafened: false })
    }
  },

  // Pick (or clear) the single "home" voice channel. Stored on user.settings
  // (channelId + serverId so the jump can cross servers). Optimistic update +
  // persist with a full-settings spread, mirroring togglePinnedVoiceRoom.
  setHomeVoiceChannel: async (channelId, serverId) => {
    const user = useAuthStore.getState().user
    if (!user) return
    const settings = user.settings ?? {}
    const next = {
      ...settings,
      homeVoiceChannelId: channelId,
      homeVoiceServerId: channelId ? serverId : null,
    }
    set({ user: { ...user, settings: next } })
    try {
      const res = await authApi.updateMe({ settings: next })
      set({ user: res.data })
    } catch (err) {
      console.error('setHomeVoiceChannel error:', err)
      useToastStore.getState().showToast({
        id: `homechan-${Date.now()}`,
        type: 'error',
        title: 'Home channel not saved',
        body: 'Please try again.',
      })
      set({ user: { ...user, settings } })
    }
  },

  toggleDeafen: async () => {
    const user = useAuthStore.getState().user
    if (!user) return
    const { isGuest } = useGuestStore.getState()

    // Guest path: deafen = mute all remote audio, undeafen = restore volumes
    if (isGuest) {
      const isCurrentlyDeafened = useAuthStore.getState().preDeafenMuted !== undefined
        ? true
        : false
      // Use a simple flag stored in preDeafenMuted for guests
      const nextDeafened = !isCurrentlyDeafened
      const { currentChannelId } = useVoiceStore.getState()

      // OSPROD-61: deafen drives BRB. Camera-off side-effect lives
      // inside setLocalAway so every BRB-activation path triggers it.
      useVoiceStore.getState().setLocalAway(nextDeafened)

      // Silencing remotes is the Web Audio GainNode's job — it reads
      // `user.settings.isDeafened` from this store and snaps gain.value
      // to 0. Guests don't hit the API (no persistent profile), so we
      // mirror the flag locally on the user object so the GainNode
      // reacts. Previously the guest branch silenced remotes via
      // `participant.setVolume(0)` directly on LiveKit, but: (a) the
      // matching un-deafen call passed peerVolume (>1× legal) which
      // crashed with IndexSizeError on HTMLMediaElement, and (b) it
      // only affected the hidden keep-alive <audio> we attach on
      // TrackSubscribed — Web Audio kept playing. Driving deafen
      // through the gain node is both safer and correct.
      const settings = user.settings ?? {}
      set({
        preDeafenMuted: nextDeafened ? true : false,
        user: { ...user, settings: { ...settings, isDeafened: nextDeafened } },
      })
      if (currentChannelId) {
        getSocket().emit('voice:state:update', { channelId: currentChannelId, isDeafened: nextDeafened })
      }
      if (nextDeafened) sounds.deafen()
      else sounds.undeafen()
      return
    }

    const settings = user.settings ?? {}
    const nextDeafened = !settings.isDeafened
    const isMuted = settings.isMuted ?? false
    const room = useLivekitStore.getState().room

    // Optimistic update
    if (nextDeafened) {
      set({
        preDeafenMuted: isMuted,
        user: { ...user, settings: { ...settings, isDeafened: true, isMuted: true } }
      })
    } else {
      const preMuted = useAuthStore.getState().preDeafenMuted
      set({ user: { ...user, settings: { ...settings, isDeafened: false, isMuted: preMuted } } })
    }
    // OSPROD-61: deafen drives BRB. Camera-off side-effect lives
    // inside setLocalAway so every BRB-activation path triggers it.
    useVoiceStore.getState().setLocalAway(nextDeafened)

    const runAsync = async () => {
      try {
        if (nextDeafened) {
          const res = await authApi.updateMe({ settings: { isDeafened: true, isMuted: true } })
          set({ user: res.data })

          if (room) {
            const actualEnabled = await mediaManager.setMicrophoneEnabled(room, false)
            // Remote silencing handled by the Web Audio GainNode in
            // LiveKitBackgroundAudio (reads isDeafened). See the
            // matching comment in the guest branch above.
            useLivekitStore.setState({ isAudioTrackReady: actualEnabled })
          }

          const { currentChannelId } = useVoiceStore.getState()
          if (currentChannelId) {
            getSocket().emit('voice:state:update', { channelId: currentChannelId, isDeafened: true, isMuted: true })
          }
          sounds.deafen()
        } else {
          const preMuted = useAuthStore.getState().preDeafenMuted
          const res = await authApi.updateMe({ settings: { isDeafened: false, isMuted: preMuted } })
          set({ user: res.data })

          if (room) {
            const actualEnabled = await mediaManager.setMicrophoneEnabled(room, !preMuted)
            // Volume restore handled by Web Audio GainNode (reads
            // isDeafened from authStore + peerVolume from voiceStore).
            // Previously did participant.setVolume(peerVolume) here,
            // but that (a) crashed on boosted volumes (>1× — slider
            // permits up to 2×; HTMLMediaElement.volume is [0,1]) and
            // (b) un-silenced the hidden keep-alive <audio> attached
            // in livekitStore.ts on TrackSubscribed, producing a
            // double playback ("everyone heard with a few-ms delay").
            useLivekitStore.setState({ isAudioTrackReady: actualEnabled })
          }

          const { currentChannelId } = useVoiceStore.getState()
          if (currentChannelId) {
            getSocket().emit('voice:state:update', { channelId: currentChannelId, isDeafened: false, isMuted: preMuted })
          }
          sounds.undeafen()
        }
      } catch (err) {
        console.error('toggleDeafen async error:', err)
        try {
          const res = await authApi.me()
          set({ user: res.data })
        } catch {
          set({ user: { ...user, settings } })
        }
      }
    }

    runAsync()
  },
}))
